import {
  Component,
  ElementRef,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { CardModule } from 'primeng/card';
import {
  ReactiveFormsModule,
  FormGroup,
  FormControl,
  Validators,
  ValidatorFn,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { CommonModule } from '@angular/common';
import { FilingSessionUploader } from '../../../services/mbrs/filing-session-uploader.service';
import { AuthService } from '../../../services/auth/auth.service';
import { FilingSessionLimitsService } from '../../../services/mbrs/filing-session-limits.service';
import { FileSizePipe } from '../../../utils/pipes/file-size.pipe';
import { formatFileSize } from '../../../utils/file-size.util';
import { environment } from '../../../../environments/environment';
import { ConversionEnginePickerComponent } from '../../common/conversion-engine-picker/conversion-engine-picker.component';

@Component({
  selector: 'dashboard-xbrl-form',
  imports: [
    CommonModule,
    CardModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    TooltipModule,
    DialogModule,
    FileSizePipe,
    ConversionEnginePickerComponent,
  ],
  templateUrl: './xbrl-form.component.html',
})
export class XbrlFormComponent implements OnInit {
  auditYear: string[] = [
    (new Date().getFullYear() - 1).toString(),
    new Date().getFullYear().toString(),
    (new Date().getFullYear() + 1).toString(),
  ];

  private readonly limitsService = inject(FilingSessionLimitsService);

  /**
   * The server's upload limits. Seeded with `FALLBACK_FILING_SESSION_LIMITS`, so
   * this is safe to read synchronously in a field initializer or template before
   * `GET /filing-sessions/limits` resolves. `isServerProvided()` on the service
   * distinguishes the two states.
   */
  readonly limits = this.limitsService.limits;

  readonly maxFileSize = computed(() => this.limits().maxFileSizeBytes);
  readonly maxFileCount = computed(() => this.limits().maxFileCount);
  readonly allowedExtensions = computed(() => this.limits().allowedExtensions);

  /** e.g. "50 MB" — derived from the server cap, never a hardcoded string. */
  readonly maxFileSizeLabel = computed(() =>
    formatFileSize(this.maxFileSize(), environment.fileSizeDecimalPrecision),
  );

  /** e.g. ".docx, .pdf" — the native `<input type="file">` accept attribute. */
  readonly acceptAttr = computed(() => this.allowedExtensions().join(', '));

  /** e.g. "*.docx, *.pdf" — product copy. */
  readonly supportedFormatLabel = computed(() =>
    this.allowedExtensions()
      .map((ext) => `*${ext}`)
      .join(', '),
  );

  constructor() {
    // When `/limits` lands, the cap/count/extensions all change under a form the
    // user may already have files in. Re-run the validators against the real
    // limits rather than the fallback they were checked against on selection.
    effect(() => {
      this.limits();
      this.selectedFile?.updateValueAndValidity({ emitEvent: false });
    });
  }

  ngOnInit(): void {
    // Idempotent; the route sits behind `isAuthenticatedGuard`, so the auth
    // cookie is present. Do NOT hoist this into `APP_INITIALIZER` — `/limits`
    // is auth-guarded and would 401 on the login page.
    this.limitsService.ensureLoaded();
  }

  // Developer Note (TODO): migrate isVisible/isDragging to signals. Left as booleans
  // because `[(visible)]` two-way binding with p-dialog requires a
  // model-signal conversion and template updates beyond this pass.
  isVisible: boolean = false;
  isDragging: boolean = false;

  isFileInvalid = signal<boolean>(false);
  canDropFiles = signal<boolean>(true);

  fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');
  private readonly uploader = inject(FilingSessionUploader);
  private readonly authService = inject(AuthService);

  // Re-exposes the uploader's in-flight flag so the template can read it
  // directly as `isSubmitting()`. Living on the root service means the
  // spinner state survives navigation away from the dashboard.
  readonly isSubmitting = this.uploader.isSubmitting;

  /**
   * Engine chosen for the Phase 1 extraction this upload kicks off. Null means
   * "omit the field" and the server applies its configured default — which is
   * also what happens whenever the picker is hidden.
   */
  readonly selectedEngine = signal<string | null>(null);

  onEngineChange(engine: string | null): void {
    this.selectedEngine.set(engine);
  }

  // xbrl file upload form
  fileUpload = new FormGroup({
    selectedFile: new FormControl<File[]>([], this.fileValidation()),
  });

  fileValidation(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const files = control.value as File[];
      const errors: ValidationErrors = {};

      if (!files || files.length < 2) {
        errors['minFiles'] = true;
      }

      if (files.length > this.maxFileCount()) {
        errors['maxFiles'] = true;
      }

      // Check if all files have correct format
      const invalidFormatFiles = files.filter(
        (file) => !this.isFileExtensionValid(file)
      );

      if (invalidFormatFiles.length > 0) {
        errors['invalidFormat'] = true;
      }

      const oversizedFiles = files.filter(
        (file) => file.size > this.maxFileSize()
      );

      if (oversizedFiles.length > 0) {
        // Surface the largest offending size (raw bytes) so the template
        // can render it through `| fileSize` — giving the user a concrete
        // "your largest file is 52.1 MB" hint against the server cap.
        errors['maxSize'] = {
          largestFileSize: Math.max(...oversizedFiles.map((file) => file.size)),
        };
      }

      // Check if at least one file has *FS suffix
      if (!this.findFsFile(files)) {
        errors['noFSFile'] = true;
      }

      return Object.keys(errors).length > 0 ? errors : null;
    };
  }

  get selectedFile() {
    return this.fileUpload.get('selectedFile');
  }

  isButtonDisabled(): boolean {
    return (
      this.fileUpload.invalid ||
      (this.selectedFile?.value?.length ?? 0) < 2 ||
      (this.selectedFile?.value?.length ?? 0) > this.maxFileCount()
    );
  }

  triggerFileInput() {
    // Don't allow file selection if limit is reached
    if (this.isFileLimitReached()) {
      return;
    }

    // Don't allow file selection if the user is logged out. Session *expiry* is
    // no longer judged client-side — the backend owns it (sliding or fixed) and
    // signals it via a 401 that sessionExpiryInterceptor turns into a logout.
    if (!this.authService.isAuthenticated()) {
      return;
    }

    if (this.fileInput()) {
      this.fileInput()!.nativeElement.value = '';
      this.fileInput()!.nativeElement.click();
    }
  }

  isFileExtensionValid(file: File): boolean {
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    return this.allowedExtensions().includes(ext);
  }

  findFsFile(files: File[]): File | undefined {
    return files.find((file) => {
      const lastDot = file.name.lastIndexOf('.');
      const nameWithoutExt =
        lastDot >= 0 ? file.name.substring(0, lastDot) : file.name;
      return nameWithoutExt.toLowerCase().endsWith('_fs');
    });
  }

  onFileSelected(event: Event): void {
    // Prevent file processing if the user is logged out (see triggerFileInput:
    // session expiry is backend-owned, surfaced via a 401, not judged here).
    if (!this.authService.isAuthenticated()) {
      return;
    }

    const fileInput = event.target as HTMLInputElement;
    if (fileInput.files && fileInput.files.length > 0) {
      const newFiles = Array.from(fileInput.files);
      const currentFiles = this.selectedFile?.value as File[] || [];

      // Do not allow appending if total would exceed the server's file-count cap
      if (currentFiles.length + newFiles.length > this.maxFileCount()) {
        this.selectedFile?.setErrors({ maxFiles: true });
        this.selectedFile?.markAsTouched();
        return;
      }

      this.isFileInvalid.set(false);
      this.addFiles(newFiles);
    }
  }

  addFiles(files: File[]): void {
    this.logSelectedFilesSnapshot(files);

    const currentFiles = [...((this.selectedFile?.value as File[]) || [])];
    const updatedFiles = [...currentFiles, ...files];

    this.selectedFile?.setValue(updatedFiles);
    // Changing the selection invalidates the server-side session's file list —
    // the next submit must not resume a session holding files from an earlier
    // attempt the user has since altered. The uploader reconciles the session
    // at submit time; the session itself is kept, not abandoned.
    this.uploader.markSelectionChanged();
    this.selectedFile?.markAsTouched();
    this.selectedFile?.updateValueAndValidity();
  }

  // Check if the file limit has been reached
  isFileLimitReached(): boolean {
    const currentFiles = this.selectedFile?.value as File[] || [];
    return currentFiles.length >= this.maxFileCount();
  }

  // Remove a file from the list
  removeFile(index: number): void {
    const currentFiles = [...(this.selectedFile?.value as File[])];
    currentFiles.splice(index, 1);

    this.selectedFile?.setValue(currentFiles);
    this.uploader.markSelectionChanged();

    if (this.isFileInvalid()) {
      this.isFileInvalid.set(false);
    }

    this.checkAndCloseDialog();
    this.syncSelectionInteractionState();
    this.selectedFile?.updateValueAndValidity();
  }

  removeFiles(): void {
    this.selectedFile?.setValue([]);
    this.uploader.markSelectionChanged();
    this.checkAndCloseDialog();
    this.syncSelectionInteractionState();
    this.selectedFile?.updateValueAndValidity();
    this.isFileInvalid.set(false);
  }

  /**
   * Keeps the field's touched/pristine state in step with whether anything is
   * actually selected.
   *
   * `fileValidation` reports `minFiles` and `noFSFile` against an EMPTY array,
   * and the template renders those as soon as the field is touched. So marking
   * the field touched unconditionally on removal left "At least 2 files are
   * required" and "At least one file should end with _FS" on screen after the
   * user had removed everything — an error state for a form that is back to the
   * exact value a freshly loaded dashboard shows silently.
   *
   * Emptying the selection therefore restores the initial untouched/pristine
   * state, the same reset `submitForm` already performs after a successful
   * upload. The field stays INVALID either way, so `isButtonDisabled` keeps
   * "Convert to XBRL" disabled — only the premature complaint goes away.
   */
  private syncSelectionInteractionState(): void {
    if ((this.selectedFile?.value?.length ?? 0) === 0) {
      this.fileUpload.markAsUntouched();
      this.fileUpload.markAsPristine();
      return;
    }

    this.selectedFile?.markAsTouched();
  }
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
    const currentFileCount = this.selectedFile?.value?.length ?? 0;
    const draggedFileCount = event.dataTransfer?.items?.length ?? 0;
    const total = currentFileCount + draggedFileCount;

    this.canDropFiles.set(total <= this.maxFileCount());
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = this.canDropFiles() ? 'copy' : 'none';
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    this.canDropFiles.set(true);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    this.canDropFiles.set(true);

    // Prevent file processing if the user is logged out (see triggerFileInput:
    // session expiry is backend-owned, surfaced via a 401, not judged here).
    if (!this.authService.isAuthenticated()) {
      return;
    }

    if (event.dataTransfer && event.dataTransfer.files) {
      const droppedFiles = Array.from(event.dataTransfer.files);

      const validFiles: File[] = [];
      let foundInvalid = false;

      for (const file of droppedFiles) {
        if (this.isFileExtensionValid(file)) {
          validFiles.push(file);
        } else {
          foundInvalid = true;
        }
      }

      if (foundInvalid) {
        this.isFileInvalid.set(true);
      } else {
        this.isFileInvalid.set(false);
      }

      if (validFiles.length > 0) {
        this.addFiles(validFiles);
      }
    }
  }

  showDialog() {
    this.isVisible = true;
  }

  closeDialog() {
    this.isVisible = false;
  }

  checkAndCloseDialog() {
    if (this.selectedFile?.value && this.selectedFile.value.length < 2) {
      this.isVisible = false;
    }
  }

  async submitForm(): Promise<void> {
    if (this.fileUpload.invalid) {
      this.fileUpload.markAllAsTouched();
      return;
    }

    const files = this.selectedFile?.value as File[];
    const fsFile = this.findFsFile(files);
    const fsFileWithXbrlExt = fsFile
      ? this.changeExtensionSafe(fsFile.name, '.xbrl')
      : '';

    this.logLimitsSnapshot(files);

    // Delegate the full 3-step upload to the root-provided uploader service.
    // It owns the sessionId, the async loop, and the result toast stream, so
    // the upload continues even if this component is destroyed mid-run (e.g.
    // user navigates to /mbrs/xbrl-files on a slow connection).
    const success = await this.uploader.submit(
      files,
      fsFileWithXbrlExt,
      this.selectedEngine(),
    );

    if (success) {
      this.selectedFile?.setValue([]);
      this.fileUpload.markAsUntouched();
      this.fileUpload.markAsPristine();
    }
  }

  changeExtensionSafe(fileName: string, newExt: string): string {
    return fileName.includes('.')
      ? fileName.replace(/\.[^/.]+$/, newExt)
      : fileName + newExt;
  }

  // Diagnostic log emitted right before Conversion Part 1 (create session +
  // upload files) fires. Lets QA and backend engineers confirm from the
  // browser devtools whether the client is enforcing the SERVER's limits or the
  // hardcoded fallback, and whether any selected file would be rejected locally.
  //
  // Suppressed in production builds via `environment.production` — the guard is a
  // compile-time constant so the entire method body is dead-code-eliminated.
  private logLimitsSnapshot(files: File[]): void {
    if (environment.production) {
      return;
    }

    const source = this.limitsService.isServerProvided() ? 'server' : 'fallback';
    const maxBytes = this.maxFileSize();
    const overCap = files.filter((file) => file.size > maxBytes).map((file) => file.name);

    // eslint-disable-next-line no-console
    console.groupCollapsed(`[xbrl-upload] upload limits source: ${source}`);
    // eslint-disable-next-line no-console
    console.log('Limits:', this.limits());
    // eslint-disable-next-line no-console
    console.log(`Max file size cap: ${this.maxFileSizeLabel()} (${maxBytes} bytes, decimal)`);
    // eslint-disable-next-line no-console
    console.log('Files:', files.map((file) => ({ name: file.name, bytes: file.size })));
    if (source === 'fallback') {
      // eslint-disable-next-line no-console
      console.warn('Enforcing hardcoded fallback limits — /limits was unreachable.');
    }
    if (overCap.length > 0) {
      // eslint-disable-next-line no-console
      console.warn('Files exceeding the cap:', overCap);
    }
    // eslint-disable-next-line no-console
    console.groupEnd();
  }

  // Diagnostic log emitted whenever files are added to the form (browse-dialog
  // and drag/drop both funnel through `addFiles`). Prints each file's raw byte
  // count alongside the value the template renders through `| fileSize`, so QA
  // can cross-compare "actual bytes on disk" with "what the user sees" — and,
  // on Windows, with what File Explorer claims. Those two disagree by design.
  // Suppressed in production builds (compile-time dead-code eliminated).
  private logSelectedFilesSnapshot(files: File[]): void {
    if (environment.production) {
      return;
    }

    const precision = environment.fileSizeDecimalPrecision;
    const rows = files.map((file) => ({
      name: file.name,
      rawBytes: file.size,
      displayed: formatFileSize(file.size, precision),
    }));

    // eslint-disable-next-line no-console
    console.groupCollapsed(
      `[xbrl-upload] files selected (${files.length}) — decimal (SI), precision ${precision}`,
    );
    // eslint-disable-next-line no-console
    console.table(rows);
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
}
